import React, {useCallback, useEffect} from 'react';
import {useSelector} from 'react-redux';

import uniqueId from 'lodash.uniqueid';
import {RESPONSE_NOTE_LIFETIME} from 'src/contexts/background-task-context/background-task-context-provider';
import {getAccountMcpServiceAccount} from 'src/openapi/admin/apis/GetAccountMcpServiceAccountApi';
import {updateAccountMcpServiceAccount} from 'src/openapi/admin/apis/UpdateAccountMcpServiceAccountApi';
import {useAppDispatch} from 'src/store/hooks';

import {Icons} from '@pb/kit-component-icon';
import {Status, usePushNotification} from '@pb/kit-component-push-notification';

import {
    selectMcpClientId,
    selectMcpClientIdLoading,
} from '../../services/mcp-slice';

import {SetupView} from './setup-view';

type Props = {
    accountId: number,
};

export const SetupController = ({accountId}: Props) => {
    const apiDispatch = useAppDispatch();
    const {onCreateNote} = usePushNotification();

    const mcpClientId = useSelector(selectMcpClientId);
    const mcpClientIdLoading = useSelector(selectMcpClientIdLoading);

    const fieldLabel = 'Client ID';

    useEffect(() => {
        apiDispatch(getAccountMcpServiceAccount({accountId}));
    }, [accountId, apiDispatch]);

    const onSave = useCallback(async (clientId: string) => {
        const response = await apiDispatch(
            updateAccountMcpServiceAccount({
                accountId,
                mcpServiceAccount: {value: clientId},
            }),
        );
        if (response.payload.error) {
            onCreateNote({
                id: uniqueId('mcp-client-id-save-error-'),
                status: Status.ERROR,
                buttonProps: {iconProps: {type: Icons.CLOSE}},
                autoHideTimeout: RESPONSE_NOTE_LIFETIME,
                body: () => response.payload.data?.message
                    || `Failed to save ${fieldLabel}`,
            });
        } else {
            onCreateNote({
                id: uniqueId('mcp-client-id-save-'),
                status: Status.SUCCESS,
                buttonProps: {iconProps: {type: Icons.CLOSE}},
                autoHideTimeout: RESPONSE_NOTE_LIFETIME,
                body: () => `${fieldLabel} saved successfully`,
            });
        }
    }, [accountId, apiDispatch, onCreateNote]);

    return (
        <SetupView
            mcpClientId={mcpClientId}
            loading={mcpClientIdLoading}
            onSave={onSave}
        />
    );
};
